import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/base/WorkCard";
import { profile } from "@/mocks/profile";
import { worksList } from "@/mocks/works";
import { newsList } from "@/mocks/news";

export default function ProfilePage() {
  const representativeWorks = worksList
    .filter((w) => w.roleType === "主演")
    .sort((a, b) => b.year - a.year)
    .slice(0, 4);

  const recentNews = [...newsList]
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 5);

  const infoRows = [
    { label: "出生日期", value: profile.birthday },
    { label: "国籍", value: profile.nationality },
    { label: "身高", value: profile.height },
    { label: "职业", value: profile.profession },
    { label: "经纪公司", value: profile.agency },
    { label: "出道时间", value: profile.debut },
  ];

  return (
    <SiteLayout
      title="白石茉莉奈个人资料 - 明星动态平台"
      description="白石茉莉奈个人资料页，涵盖出生日期、国籍、身高、职业、经纪公司、演艺经历、主要成就与代表作品，全面了解她的艺术生涯。"
      keywords="白石茉莉奈个人资料,白石茉莉奈简介,白石茉莉奈介绍,明星资料"
    >
      <section className="relative h-[280px] md:h-[360px] overflow-hidden bg-foreground-950">
        <img
          src={profile.hero}
          alt={`${profile.name} 个人资料背景`}
          title={`${profile.name} - 个人资料`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/50 to-foreground-950/30"></div>
      </section>

      <div className="px-4 md:px-8">
        <div className="relative -mt-20 md:-mt-24">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            <div className="lg:col-span-1">
              <div className="w-40 h-40 md:w-48 md:h-48 rounded-lg overflow-hidden border-4 border-background-50 bg-background-100">
                <img
                  src={profile.avatar}
                  alt={`${profile.name} 代表照`}
                  title={`${profile.name} - 代表照`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="lg:col-span-2 lg:pt-24">
              <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "个人资料" }]} />
            </div>
          </div>
        </div>

        <div className="mt-6 md:mt-8">
          <span className="text-xs font-body tracking-[0.3em] text-accent-600">
            PROFILE
          </span>
          <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 mt-2 tracking-wide">
            {profile.name}
          </h1>
          <p className="text-sm text-foreground-400 mt-2 font-body tracking-[0.2em]">
            {profile.nameEn}
          </p>
        </div>
      </div>

      <div className="px-4 md:px-8 py-10 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">
          <div className="lg:col-span-2 space-y-10">
            <section>
              <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-5 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-primary-500"></span>
                基本信息
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 border-t border-background-200">
                {infoRows.map((row) => (
                  <div
                    key={row.label}
                    className="flex items-start gap-4 py-4 border-b border-background-200"
                  >
                    <span className="text-sm text-foreground-400 font-body w-24 shrink-0">
                      {row.label}
                    </span>
                    <span className="text-sm text-foreground-900 font-body">
                      {row.value}
                    </span>
                  </div>
                ))}
                <div className="flex items-start gap-4 py-4 border-b border-background-200 sm:col-span-2">
                  <span className="text-sm text-foreground-400 font-body w-24 shrink-0">
                    代表作品
                  </span>
                  <span className="flex flex-wrap gap-2">
                    {representativeWorks.map((w) => (
                      <Link
                        key={w.id}
                        to={`/works/detail/${w.id}`}
                        className="text-sm font-body text-primary-600 hover:text-primary-700 underline underline-offset-4 decoration-primary-200"
                      >
                        《{w.title}》
                      </Link>
                    ))}
                  </span>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-5 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-primary-500"></span>
                演艺经历
              </h2>
              <div className="space-y-5">
                {profile.bio.map((para, index) => (
                  <p
                    key={index}
                    className="text-base text-foreground-700 leading-[1.9] font-body"
                  >
                    {para}
                  </p>
                ))}
              </div>
            </section>

            <section>
              <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-5 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                主要成就
              </h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {profile.achievements.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 bg-background-100 rounded-lg p-4"
                  >
                    <i className="ri-trophy-line text-accent-500 text-lg mt-0.5"></i>
                    <span className="text-sm text-foreground-800 font-body leading-relaxed">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-5 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                代表作品
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
                {representativeWorks.map((w) => (
                  <WorkCard key={w.id} item={w} />
                ))}
              </div>
            </section>
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 space-y-6">
              <div className="w-full aspect-[4/5] rounded-lg overflow-hidden bg-background-100 border border-background-200">
                <img
                  src={profile.portrait}
                  alt={`${profile.name} 生活照`}
                  title={`${profile.name} - 生活照`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="bg-background-50 rounded-lg border border-background-200 p-6">
                <h3 className="font-heading text-base text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-flashlight-line text-accent-500"></i>
                  最近动态
                </h3>
                <ul className="divide-y divide-background-200">
                  {recentNews.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/news/detail/${item.id}`}
                        className="group block py-3"
                      >
                        <h4 className="text-sm text-foreground-800 font-body leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {item.title}
                        </h4>
                        <span className="text-xs text-foreground-400 mt-1.5 block font-body">
                          {item.date} · {item.category}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/news"
                  className="inline-flex items-center gap-1.5 mt-4 text-sm text-primary-600 hover:text-primary-700 font-body whitespace-nowrap"
                >
                  查看全部资讯
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}